import { formatPeakWindow } from "@logic/lib/battery-arbitrage"

import type {
  BatteryArbitrageRateHoursDto,
  BatteryArbitrageRateSummaryDto,
  BatteryArbitrageResultDto,
  BatteryArbitrageSampleDayRatesDto,
} from "~/api/dto/battery-arbitrage.dto"

import { formatUsdPerKwh } from "../view-helpers"

const calendarDay = new Intl.DateTimeFormat(`en-US`, {
  month: `long`,
  day: `numeric`,
  year: `numeric`,
  timeZone: `UTC`,
})

const formatLocalDate = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return value
  return calendarDay.format(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  )
}

const formatInstant = (value: string, timezone: string): string => {
  const instant = Date.parse(value)
  if (!Number.isFinite(instant)) return value
  try {
    return new Intl.DateTimeFormat(`en-US`, {
      month: `long`,
      day: `numeric`,
      year: `numeric`,
      timeZone: timezone,
    }).format(new Date(instant))
  } catch {
    return value
  }
}

const hoursLabel = (
  hours: BatteryArbitrageRateHoursDto | null,
): string | null => (hours == null ? null : formatPeakWindow(hours))

const tariffLabel = (
  summary: Extract<BatteryArbitrageRateSummaryDto, { kind: `available` }>,
): string => {
  const tariffParts = [summary.tariffName, summary.tariffCode].filter(Boolean)
  if (tariffParts.length === 0) return `Not named in the saved estimate`
  return [...new Set(tariffParts)].join(` · `)
}

const SampleDay = ({
  sampleDay,
  coverage,
}: {
  sampleDay: BatteryArbitrageSampleDayRatesDto
  coverage: `full_saved_period` | `sample_day_only`
}): JSX.Element => {
  const cheapest = hoursLabel(sampleDay.cheapestHours)
  const expensive = hoursLabel(sampleDay.expensiveHours)
  return (
    <div className="flex flex-col gap-1.5">
      <p>
        On the sample day of {formatLocalDate(sampleDay.localDate)}, modeled
        import rates ranged from{` `}
        {formatUsdPerKwh(sampleDay.lowestModeledImportUsdPerKwh)} to{` `}
        {formatUsdPerKwh(sampleDay.highestModeledImportUsdPerKwh)}.
        {coverage === `full_saved_period`
          ? ` That day is a sample of the first 24 hours of the saved period. It is not the full-year range.`
          : ` Only this sample day has saved rate evidence. It is not a full-period range.`}
      </p>
      {cheapest ? <p>Cheaper charging hours that day: {cheapest}.</p> : null}
      {expensive ? (
        <p>More expensive discharge hours that day: {expensive}.</p>
      ) : null}
    </div>
  )
}

export const BatterySavingsRateSummary = ({
  result,
}: {
  result: BatteryArbitrageResultDto
}): JSX.Element => {
  const {
    rateSummary = { kind: `unavailable`, reason: `insufficient_saved_evidence` },
  } = result
  let body: JSX.Element
  switch (rateSummary.kind) {
    case `unavailable`:
      body = (
        <p className="mt-2 text-[length:var(--step--2)] leading-snug text-foreground-soft">
          Rate details for this saved estimate are unavailable.
        </p>
      )
      break
    case `available`:
      body = (
        <div className="mt-2 flex flex-col gap-2 text-[length:var(--step--2)] leading-snug text-foreground-soft">
          <p>
            Utility Company:{` `}
            {rateSummary.utilityCompanyName ?? `Not named in the saved estimate`}
          </p>
          <p>Selected tariff: {tariffLabel(rateSummary)}</p>
          <p>
            Source: saved {rateSummary.source.system} rate plan, revision{` `}
            {rateSummary.source.version}.
          </p>
          <p>
            Calculation period:{` `}
            {formatInstant(rateSummary.fromDateTime, rateSummary.timezone)}
            {` `}
            through {formatInstant(rateSummary.toDateTime, rateSummary.timezone)}
            {` `}({rateSummary.timezone}).
          </p>
          {rateSummary.coverage === `full_saved_period` ? (
            <>
              <p>
                Lowest modeled import rate for the covered period:{` `}
                {formatUsdPerKwh(rateSummary.lowestModeledImportUsdPerKwh)}.
              </p>
              <p>
                Highest modeled import rate for the covered period:{` `}
                {formatUsdPerKwh(rateSummary.highestModeledImportUsdPerKwh)}.
              </p>
              {rateSummary.variation.includes(`seasonal`) ? (
                <p>
                  Modeled import rates were higher in some months than others.
                </p>
              ) : null}
              {rateSummary.variation.includes(`time_of_day`) ? (
                <p>Modeled import rates also change by hour of day.</p>
              ) : null}
              {rateSummary.summarizedSeries === `before_and_after_import` ? (
                <p>
                  After-battery bills used different hourly import rates than the
                  before-battery bills, which happens when usage tiers change.
                  The range above covers both series.
                </p>
              ) : (
                <p>
                  These figures summarize the before-battery modeled import
                  rates.
                </p>
              )}
              {rateSummary.sampleDay ? (
                <SampleDay
                  sampleDay={rateSummary.sampleDay}
                  coverage="full_saved_period"
                />
              ) : null}
            </>
          ) : (
            <SampleDay
              sampleDay={rateSummary.sampleDay}
              coverage="sample_day_only"
            />
          )}
          <p>
            Estimated savings are the modeled before and after bill difference.
            They also depend on household usage, battery capacity, backup
            reserve, and efficiency. These modeled import rates do not include
            every fixed charge or export credit.
          </p>
        </div>
      )
      break
    default: {
      const _exhaustive: never = rateSummary
      throw new Error(`Unhandled rate summary ${_exhaustive}`)
    }
  }
  return (
    <section
      aria-label="Rates used in this estimate"
      data-testid="arbitrage-rate-summary"
      className="rounded-lg border border-bp-border bg-bp-elevated px-4 py-3"
    >
      <h3 className="font-display text-[length:var(--step-0)] font-semibold text-foreground">
        Rates used in this estimate
      </h3>
      {body}
    </section>
  )
}
